import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, ChatCircle, CircleNotch } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Button } from '../../ui/button';

import { campaignCopy } from '@discovr/features';
import { isTextAnchor, type AssignedProduct, type ScriptDoc } from '@discovr/types';

import { cn } from '../../../lib/utils';
import { MODAL_SCRIM_CLASS } from '../../ui/modalScrim';
import { useBodyScrollLock } from '../../../lib/useBodyScrollLock';
import type { ReviewThreadEntry, TextMessageAnchor } from '../../../lib/models';
import type { GeoAiEnvelope, ScriptReviewAiEnvelope } from './types';
import {
  ScriptResultTabs,
  type AddToCommentPayload,
  type SelectCommentPayload,
} from './ScriptResultTabs';
import { ChatThread, type AddCommentPayload } from '../ChatThread';
import { CommentWalkthrough } from './CommentWalkthrough';
import { ReviewStatusPill } from '../ReviewStatusPill';
import { UnreadBadge } from '../UnreadBadge';
import { SharePopover } from '../../../campaign-detail/share/SharePopover';
import { useCampaignShare } from '../../../campaign-detail/share/useCampaignShare';
import { purposeForPanel, type ShortShareInput } from '../../../campaign-detail/share/shortLink';
import { usePublicView } from '../../../campaign-detail/publicView';
import type { CampaignSearch } from '../../../campaign-detail/search';

export type { ScriptReviewAiEnvelope } from './types';

// V2: the script is rendered inline in this panel (via ScriptResultTabs);
// there is no separate "Open script" external link. The header pills row
// keeps just the Thread toggle.

export type ScriptReviewPanelProps = {
  open: boolean;
  onClose: () => void;

  creatorName: string;
  creatorAvatarUrl?: string;
  creatorHandle?: string;
  /** Platform username for the admin share deep link (distinct from the
   *  presentational `creatorHandle`, which may be the display name). */
  shareCreatorUsername?: string;
  /** Override the tab embedded in the share URL (defaults to 'scripts').
   *  Pass 'confirmed' when rendering this panel from the Finalized tab. */
  shareTab?: NonNullable<CampaignSearch['tab']>;
  /** Override the panel value embedded in the share URL (defaults to
   *  'script-review' for the live review surface). Pass 'script-finalized'
   *  when rendering this panel from the Finalized tab so a shared link routes
   *  back to the read-only surface — not the live workflow review. */
  sharePanel?: 'script-review' | 'script-finalized';

  statusLabel?: string;
  statusTone?: 'default' | 'pending' | 'approved' | 'rejected' | 'revision';
  uploadedLabel?: string;
  versionLabel?: string;

  /** Products assigned to this creator (Multi-Product Campaigns), resolved from
   *  ScriptDetail.assigned_products. Rendered as read-only "Featuring" chips in
   *  the header — identical for brand and admin. */
  assignedProducts?: AssignedProduct[];
  /** Campaign product-catalog size — gates the Featuring chips to >= 2 products
   *  (0-1 product campaigns render no chips, exactly as before). */
  campaignProductCount?: number;

  scriptText?: string;
  /** The script's native document (Lexical), rendered read-only. */
  doc?: ScriptDoc;
  /** The script text's own fetch is in flight — distinct from aiAnalysis
   *  status. Being on script review implies a script exists; while this is
   *  true, an empty scriptText renders a skeleton instead of the "No
   *  script text yet" empty state, which otherwise flashes on every open. */
  scriptTextLoading?: boolean;
  scriptPdfUrl?: string;

  aiAnalysis?: ScriptReviewAiEnvelope;
  onRunAiReviewAgain?: () => void;
  aiReviewAgainBusy?: boolean;

  /** GEO optimizer pack envelope (ScriptDetail.geo_analysis). showGeo gates the
   *  "GEO Optimized" tab to YouTube campaigns; onGenerateGeo triggers a pack. */
  geoAnalysis?: GeoAiEnvelope;
  showGeo?: boolean;
  onGenerateGeo?: () => void | Promise<void>;
  geoGenerating?: boolean;
  /** Regenerate a GEO element ('' = whole pack) the brand didn't like. */
  onRegenerateGeo?: (element: string) => void | Promise<void>;
  /** Auto-save the approved GEO per-element selection on each toggle. */
  onSaveGeoSelection?: (selected: string[]) => void | Promise<void>;
  /** Read-only GEO surface (Confirmed/finalized tab): renders the approved
   *  pack + approved selection (highlighted) with no editing controls.
   *  Defaults to false so live review callers are unaffected. */
  geoReadOnly?: boolean;

  thread?: ReviewThreadEntry[];
  /** The FULL, unpaginated set of anchored messages for the current script
   *  version (GET .../threads/script/anchors) — backs the Script tab's
   *  persistent highlights. `thread` above is chat-paginated (default 20),
   *  so filtering it for anchors silently drops highlights for comments
   *  older than the most recent page on any thread with real back-and-forth
   *  review. Omit to fall back to filtering `thread` (pre-existing
   *  behavior) — callers that haven't wired the new query yet still work,
   *  just with the same pagination limit as before. */
  openAnchorMessages?: ReviewThreadEntry[];
  /** V2 (2026-05-04): unread message count for the viewer on this
   *  thread — drives the red pill on the Thread toggle button.
   *  Falsy / omitted suppresses the pill entirely. Decoupled from
   *  `thread.length` (total) so the header reflects "what's new for
   *  you" rather than the conversation's full history. */
  unreadCount?: number;
  canAddComment?: boolean;
  /** Thread is locked (approved -> read-only). Forwarded to ChatThread so
   *  Edit/Delete gate on the LOCK rather than on `canAddComment`, which at some
   *  call sites also encodes "is a post handler wired?" and would otherwise
   *  strip both affordances from an open thread. Omitted: ChatThread falls back
   *  to `canAddComment === false`. */
  locked?: boolean;
  addCommentBusy?: boolean;
  onAddComment?: (payload: AddCommentPayload) => void | Promise<void>;
  onEditMessage?: (id: string, body: string) => void | Promise<void>;
  onDeleteMessage?: (id: string) => void | Promise<void>;
  /** True only for the Super Admin tier — lets ChatThread's delete
   *  affordance bypass the locked-thread gate (server permission flags
   *  already scope who may actually delete what). */
  viewerIsSuperAdmin?: boolean;
  /** Current script version id (ScriptDetail.version_id) — stamped onto
   *  quote-reply anchors so comments stay pinned to the revision they
   *  were made against when the script is later replaced. */
  scriptVersionId?: string;
  /** Script version archive for the Script tab dropdown (newest first). */
  scriptVersions?: Array<{ id: string; version: number; current?: boolean }>;
  /** Fetches an archived revision's markdown (read-only viewing). */
  onFetchVersionText?: (versionId: string) => Promise<string>;
  /** Gates the Line-by-Line tab's per-row "Add to Comment" button. Distinct
   *  from `canAddComment` (which gates the thread compose box itself) — pass
   *  `false` once the script's production status has moved past
   *  `script_review` (e.g. `content_pending`) so a stale suggestion can't be
   *  appended to a review that's already over. Defaults to `true`. */
  canAddLineComment?: boolean;
  /** Viewer's actor — drives left/right alignment in the thread column. Defaults to 'brand'. */
  viewerActor?: string;

  canReview?: boolean;
  reviewBusy?: boolean;
  /** Green "Approved" button shown after a successful approve — keeps panel open. */
  isApproved?: boolean;
  // V2: only state-changing review action is approve (transitions
  // *_review → next phase's pending). "Request changes" is now a chat-
  // thread comment (no status change). Reject pathway is removed; the
  // formal reject statuses script_rejected / content_rejected don't
  // exist anymore.
  onApprove?: () => void;

  /** Runs the 3-step comments walkthrough inside the panel. Owned by the
   *  mounting tab (page-level intro modal + once-per-browser gating live
   *  there); Finish/× report back via onWalkthroughDone. */
  walkthroughActive?: boolean;
  onWalkthroughDone?: () => void;

  title?: string;
  threadDefaultOpen?: boolean;
  /** Called whenever the thread column toggles (open or close). Use to mirror
   *  the state to `?thread=open` so refresh restores the panel identically. */
  onThreadOpenChange?: (open: boolean) => void;

  /** D5: when true, the conversation toggle button itself is not rendered
   *  at all (not just disabled) — used when a public grant's
   *  allow_chat_readonly was off at mint time, so there is genuinely
   *  nothing to show, not an empty state to reveal. Defaults false — every
   *  existing authed caller is unaffected. */
  hideThreadToggle?: boolean;
};




// ─── Main panel (preserved behavior, wires to new AnalysisColumn) ────────────

const NO_PRODUCTS_NOTICE = campaignCopy.creator.previousProducts.noProductsOnApproval;

export function ScriptReviewPanel({
  open,
  onClose,
  creatorName,
  creatorAvatarUrl,
  creatorHandle,
  shareCreatorUsername,
  shareTab: _shareTab = 'scripts',
  sharePanel = 'script-review',
  statusLabel,
  statusTone = 'default',
  uploadedLabel: _uploadedLabel,
  versionLabel: _versionLabel,
  assignedProducts,
  campaignProductCount,
  scriptText,
  doc,
  scriptTextLoading,
  scriptPdfUrl: _scriptPdfUrl,
  aiAnalysis,
  geoAnalysis,
  showGeo,
  onGenerateGeo,
  geoGenerating,
  onRegenerateGeo,
  onSaveGeoSelection,
  geoReadOnly,
  thread,
  openAnchorMessages,
  unreadCount,
  canAddComment,
  locked,
  addCommentBusy,
  onAddComment,
  onEditMessage,
  onDeleteMessage,
  viewerIsSuperAdmin,
  scriptVersionId,
  scriptVersions,
  onFetchVersionText,
  canAddLineComment = true,
  viewerActor = 'brand',
  canReview,
  reviewBusy,
  isApproved,
  onApprove,
  walkthroughActive = false,
  onWalkthroughDone,
  title = 'Script review',
  threadDefaultOpen = false,
  onThreadOpenChange,
  hideThreadToggle = false,
}: ScriptReviewPanelProps) {
  const { canShare, makeShortUrl } = useCampaignShare();
  const { isPublicView } = usePublicView();
  const shareInput: ShortShareInput | null = shareCreatorUsername
    ? {
        kind: 'creator',
        username: shareCreatorUsername,
        purpose: purposeForPanel(sharePanel) ?? 'script',
        // No `sub` here (2026-08-05 "one link per review panel" decision):
        // a script grant already returns the FULL panel — every internal tab
        // (overview/script/lineby/variants/safety/geo) — from one complete
        // payload, so pinning the mint to whatever `?stab=` happened to be
        // open would only fragment one panel's shares across multiple grant
        // keys for no product benefit. `sub` stays meaningful for `insights`
        // (buildInsightsView genuinely branches on it), just not here.
      }
    : null;
  const [threadOpen, setThreadOpen] = useState<boolean>(
    threadDefaultOpen || (Array.isArray(thread) && thread.length > 0),
  );
  const [draft, setDraft] = useState<string>('');
  const [stagedAnchor, setStagedAnchor] = useState<TextMessageAnchor | undefined>(undefined);

  const dismissWalkthrough = useCallback(() => {
    onWalkthroughDone?.();
  }, [onWalkthroughDone]);
  // Step 1 teaches highlighting → the card hovers the script area;
  // step 2 (the note) anchors over the chat box above the composer
  // (right 20 / bottom 80); step 3 (the thread) moves to the TOP of the
  // chat box (right 20 / top 15) — both per the Figma mocks.
  const [walkthroughStep, setWalkthroughStep] = useState(0);
  const draftTextareaRef = useRef<HTMLTextAreaElement>(null);

  // Chat column width: fixed, not user-resizable — a drag-to-resize handle
  // used to sit on the chat's left edge, but it was removed (product
  // decision: the chat panel is not draggable). 360px matches the width
  // this panel used in production before the (now-removed) resizable
  // column was introduced.
  const CHAT_WIDTH = 360;

  // Cache the last-known-good AI analysis so a refetch (e.g. after the user
  // posts a thread comment, which invalidates the same query that holds the
  // analysis) doesn't briefly flash the panel back to a "running…" spinner.
  // We only re-derive when a real new completed analysis arrives.
  // Cache held in state (not a ref) so it isn't read/written during render.
  // Adjusted during render via an equality guard: cache the latest terminal
  // analysis while open, and clear it when the slide-over is closed so a
  // different creator opens with a fresh state.
  const [lastAnalysis, setLastAnalysis] = useState<typeof aiAnalysis | undefined>(
    undefined,
  );
  const isTerminalAnalysis =
    (aiAnalysis?.status === 'complete' && !!aiAnalysis.data) ||
    aiAnalysis?.status === 'failed';
  if (!open) {
    if (lastAnalysis !== undefined) setLastAnalysis(undefined);
  } else if (isTerminalAnalysis && aiAnalysis !== lastAnalysis) {
    setLastAnalysis(aiAnalysis);
  }
  const stableAnalysis =
    aiAnalysis?.status === 'pending' && lastAnalysis ? lastAnalysis : aiAnalysis;

  // "Add to Comment" rows in ScriptResultTabs route here: stage a quote-reply
  // chip above the composer (the row's ORIGINAL text — the client is replying
  // to the line, not endorsing the AI's fix), pre-fill the draft with the
  // row's own Issue identified / Suggested edit (still fully editable —
  // this only saves retyping what the row already showed, it doesn't lock
  // the client into the AI's wording), open the thread panel, and focus the
  // input.
  const handleAddToChat = useCallback((payload: AddToCommentPayload) => {
    const quoted = payload.originalText.trim();
    if (!quoted) return;
    setStagedAnchor({
      quoted_text: quoted,
      aspect: payload.aspect || undefined,
      origin: 'row',
      script_version_id: scriptVersionId || undefined,
    });
    const draftParts: string[] = [];
    if (payload.issue) draftParts.push(`Issue: ${payload.issue}`);
    if (payload.suggestion) draftParts.push(`Suggested edit: ${payload.suggestion}`);
    if (draftParts.length > 0) setDraft(draftParts.join('\n\n'));
    setThreadOpen(true);
    onThreadOpenChange?.(true);
    setTimeout(() => draftTextareaRef.current?.focus(), 50);
  }, [onThreadOpenChange, scriptVersionId]);

  // Open-comment anchors for the Script tab's persistent highlights.
  // Prefer openAnchorMessages (the full, unpaginated per-version set) when
  // the caller has wired it — falls back to filtering the chat-paginated
  // `thread` for callers that haven't yet (same pre-existing behavior,
  // including its "silently misses anchors past the first page" limit).
  // Version-scoped either way: an anchor pinned to a different script
  // revision is excluded (its text may no longer exist); pinless anchors
  // still try.
  const openAnchors = useMemo(
    () =>
      (openAnchorMessages ?? thread ?? [])
        .flatMap((m) => {
          const anchor = m.anchor;
          if (!isTextAnchor(anchor)) return [];
          const matchesVersion =
            !anchor.script_version_id || !scriptVersionId || anchor.script_version_id === scriptVersionId;
          if (!matchesVersion) return [];
          return [{ quoted_text: anchor.quoted_text, occurrence: anchor.occurrence, messageId: m.id }];
        }),
    [openAnchorMessages, thread, scriptVersionId],
  );

  // Clicking an anchored quote in the chat jumps the Script tab to that
  // line. Same version guard as openAnchors above, but here it has to be
  // a hard stop rather than a silent filter — the user just clicked
  // something, so silently doing nothing would read as broken rather
  // than as "this comment is from an earlier revision."
  //
  // nonce, not just the quote text: ScriptResultTabs edge-triggers off a
  // CHANGE in this value to stay one-shot (so repeat clicks can retrigger
  // rather than being silently swallowed by a stale "already consumed"
  // check) — clicking the SAME anchor twice in a row sends the same text
  // both times, which a plain string can't distinguish from "haven't
  // consumed this yet". The nonce always changes, even then.
  const [jumpTarget, setJumpTarget] = useState<{
    text: string;
    occurrence: number;
    nonce: number;
  } | null>(null);
  const jumpNonceRef = useRef(0);
  const handleJumpToAnchor = useCallback(
    (anchor: TextMessageAnchor) => {
      if (
        anchor.script_version_id &&
        scriptVersionId &&
        anchor.script_version_id !== scriptVersionId
      ) {
        toast.info('This comment refers to an earlier version of the script.');
        return;
      }
      jumpNonceRef.current += 1;
      // occurrence, NOT just text: the persistent purple highlight already
      // resolves anchors by (quoted_text, occurrence) — see the highlightAnchors
      // mapping below — and this jump target must agree with it. A script
      // with the SAME phrase twice (a repeated tagline/CTA) would otherwise
      // always jump to the FIRST occurrence regardless of which one the
      // comment was actually anchored to, visibly disagreeing with its own
      // persistent highlight sitting on the correct one.
      setJumpTarget({
        text: anchor.quoted_text,
        occurrence: anchor.occurrence ?? 0,
        nonce: jumpNonceRef.current,
      });
    },
    [scriptVersionId],
  );
  // Stable identity — an inline arrow here would be a new function on every
  // render of THIS panel, and it's a dependency of ScriptResultTabs' own
  // jump-consumption effect, so it'd tear that effect down and re-run it
  // on every unrelated re-render, not just when jumpTarget is consumed.
  const handleJumpedToQuote = useCallback(() => setJumpTarget(null), []);

  // Script-tab select-to-comment (PRD §4.2): same chip flow, selection
  // origin. Untagged by design — the client picks a tag later (Phase 3);
  // never guess one from a raw selection.
  const handleSelectToComment = useCallback((payload: SelectCommentPayload) => {
    setStagedAnchor({
      quoted_text: payload.quotedText,
      origin: 'selection',
      occurrence: payload.occurrence || undefined,
      script_version_id: scriptVersionId || undefined,
    });
    setThreadOpen(true);
    onThreadOpenChange?.(true);
    setTimeout(() => draftTextareaRef.current?.focus(), 50);
  }, [onThreadOpenChange, scriptVersionId]);

  // "View comment" (Script tab → chat): the reverse of handleJumpToAnchor
  // above. Nonce-keyed for the same reason jumpTarget is — clicking the
  // SAME already-hovered comment twice in a row must still re-trigger the
  // scroll+flash, not get silently swallowed as "no change".
  const [scrollToMessage, setScrollToMessage] = useState<{ id: string; nonce: number } | null>(
    null,
  );
  const scrollToMessageNonceRef = useRef(0);
  const handleViewComment = useCallback(
    (messageId: string) => {
      setThreadOpen(true);
      onThreadOpenChange?.(true);
      scrollToMessageNonceRef.current += 1;
      // Deferred, like handleSelectToComment's own focus() above: the chat
      // column is width-animating open (or already open) and the target
      // bubble needs a layout pass before scrollIntoView has anything
      // meaningful to scroll to.
      setTimeout(() => {
        setScrollToMessage({ id: messageId, nonce: scrollToMessageNonceRef.current });
      }, 50);
    },
    [onThreadOpenChange],
  );

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      // CommentWalkthrough owns its own Escape-to-dismiss via Radix's Dialog
      // (a capture-phase document listener that fires before this one) — just
      // suppress the panel's own close so the same keystroke doesn't also
      // close the whole panel underneath the tour.
      if (walkthroughActive) return;
      onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose, walkthroughActive]);

  // Keep the draft empty whenever the drawer is closed (adjusted during render,
  // guarded so it doesn't loop) so it reopens fresh — no effect needed.
  if (!open && draft !== '') setDraft('');
  if (!open && stagedAnchor !== undefined) setStagedAnchor(undefined);

  // This panel portals itself directly (createPortal below) rather than going
  // through Radix Dialog/Sheet, so it doesn't get Radix's built-in body-scroll
  // lock for free — without this, the background table stays independently
  // scrollable (and shows its own scrollbar) while the panel is open on top
  // of it, which is not standard modal/drawer behavior.
  useBodyScrollLock(open);

  if (!open || typeof document === 'undefined') return null;

  // Drawer width — a little wider than the Figma "rail" (724px) so the full tab
  // row fits without a horizontal scroll, incl. the GEO Optimized tab (YouTube
  // campaigns) which pushes the tab row past the Figma minimum. The review
  // content's actual floor is 870px (see min-w-[870px] inner wrapper below) —
  // the drawer itself is sized 24px WIDER than that floor (894, not 870) so
  // the vertical scrollbar's own width (this panel is almost always tall
  // enough to scroll) has room to eat into the available box without ever
  // pushing it below 870. A previous attempt bumped BOTH numbers to the same
  // value (888/888), which left zero margin and reintroduced the exact same
  // horizontal-scroll bug it was meant to fix — the two numbers must differ.
  // When the chat opens, the drawer widens by the 360px chat column on top of
  // that same 894 floor. At viewports too narrow to show both at full size,
  // the content area scrolls horizontally rather than compressing.
  // Plain inline style, not a Tailwind arbitrary-value class — Tailwind's JIT
  // never generated a rule for the nested min()/calc() class here (verified:
  // no matching rule in any loaded stylesheet, regardless of the calc()
  // whitespace fix), so the panel silently fell back to auto/content width.
  // An inline style has no such ambiguity. max-[992px]:w-full! (below) still
  // wins on narrow viewports via !important, which beats an inline style.
  // Read from the detail's own assigned_products, so it never depends on an approval-response flag.
  const showNoProductsNotice =
    !!canReview &&
    !!onApprove &&
    !isApproved &&
    (campaignProductCount ?? 0) >= 2 &&
    assignedProducts !== undefined &&
    assignedProducts.length === 0;

  const panelWidth = threadOpen
    ? { width: `min(${894 + CHAT_WIDTH}px, calc(100vw - 3rem))` }
    : { width: 'min(894px, calc(100vw - 3rem))' };

  return createPortal(
    <>
      <div
        className={MODAL_SCRIM_CLASS}
        onClick={onClose}
        aria-hidden="true"
        // Inert everywhere except the public share shell, which reads this
        // attribute in its click-capture allow-list — see
        // PublicCampaignShell.tsx's file header. Lets the scrim's own
        // onClose={onClose} fire there instead of being silently swallowed;
        // does nothing in the authed app (no capture listener reads it).
        data-public-allowed-subtree=""
      />

      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        style={panelWidth}
        className="fixed inset-y-0 right-0 z-50 flex flex-col bg-card shadow-[-10px_0_30px_rgba(15,23,42,0.15)] transition-[width] duration-300 ease-in-out max-[992px]:w-full!"
        // Same as the scrim above: lets every read-only interaction inside
        // THIS granted panel (tab switches, accordions, GEO toggles) work
        // normally on a public link, while onClose/onApprove etc. still show
        // the login prompt because THEY, not this attribute, call
        // onRequestLogin — see PublicCampaignShell.tsx.
        data-public-allowed-subtree=""
      >
        {/* Header mirrors ContentReviewPanel + Figma 1672-30625: avatar + name/
            handle (left), then black Approve, an icon-only conversation button,
            and close (right). */}
        <header className="flex h-[70px] shrink-0 items-center gap-3 border-b border-border-subtle bg-card px-5">
          <div
            className="size-9 shrink-0 rounded-full border border-border bg-muted bg-cover bg-center"
            style={creatorAvatarUrl ? { backgroundImage: `url(${creatorAvatarUrl})` } : undefined}
            aria-hidden
          />
          <div className="flex min-w-0 flex-col gap-0.5">
            <div className="flex items-center gap-2">
              <span className="truncate text-body-sm-regular/5 font-medium text-foreground">{creatorName}</span>
              {statusLabel ? <ReviewStatusPill label={statusLabel} tone={statusTone} /> : null}
            </div>
            {creatorHandle ? (
              <span className="truncate text-caption-regular/4 font-medium text-muted-foreground">{creatorHandle}</span>
            ) : null}
          </div>

          <div className="ml-auto flex items-center gap-2">
            {isApproved ? (
              <Button
                variant="default"
                type="button"
                disabled
                className="inline-flex h-10 items-center gap-1.5 rounded-full bg-green-600 px-5 text-sm font-medium text-white opacity-100 cursor-default"
              >
                Approved
              </Button>
            ) : canReview && onApprove ? (
              <Button
                variant="default"
                type="button"
                onClick={onApprove}
                disabled={reviewBusy}
                className="inline-flex h-10 items-center gap-1.5 rounded-full bg-foreground px-5 text-sm font-medium text-white transition-colors hover:bg-foreground/90 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {reviewBusy ? <CircleNotch size={12} className="animate-spin" /> : null}
                Approve
              </Button>
            ) : null}
            {!hideThreadToggle && (
              <Button
                variant="outline"
                size="icon"
                type="button"
                onClick={() =>
                  setThreadOpen((v) => {
                    const next = !v;
                    onThreadOpenChange?.(next);
                    return next;
                  })
                }
                className={cn(
                  'relative size-10 shrink-0 rounded-full border p-0',
                  threadOpen
                    ? 'border-foreground bg-muted text-foreground hover:bg-muted'
                    : 'border-border bg-card text-foreground hover:bg-accent',
                )}
                aria-pressed={threadOpen}
                aria-label="Conversation"
              >
                <ChatCircle size={16} />
                {unreadCount ? (
                  <span className="absolute -right-1 -top-1">
                    <UnreadBadge count={unreadCount} variant="inline" toneClassName="bg-rose-500" />
                  </span>
                ) : null}
              </Button>
            )}
            {/* `!isPublicView`: keeps SharePopover's authed useShareGrants
                query unreachable from the public shell by construction — see
                the same guard in DrawerHeader. */}
            {canShare && shareCreatorUsername && !isPublicView ? (
              <SharePopover
                input={shareInput}
                getUrl={() => (shareInput ? makeShortUrl(shareInput) : null)}
                label="Copy link to this view"
              />
            ) : null}
            {/* Public (no-login) share view: the panel IS the shared surface,
                so there is nothing to close back to — hide the control. */}
            {!isPublicView && (
              <Button
                variant="ghost"
                size="icon"
                type="button"
                onClick={onClose}
                className="inline-flex size-9 items-center justify-center rounded-lg border border-border text-muted-foreground hover:bg-accent"
                aria-label="Close"
              >
                <X size={18} />
              </Button>
            )}
          </div>
        </header>
        {showNoProductsNotice ? (
          <p role="status" className="shrink-0 border-b border-border-subtle bg-muted/40 px-5 py-2 text-caption-regular text-muted-foreground">
            {NO_PRODUCTS_NOTICE}
          </p>
        ) : null}

        {/* Body: review content (left, scrolls independently) + chat (right, slides in). */}
        <div className="relative flex flex-1 min-h-0 overflow-hidden">
          {walkthroughActive ? (
            <div
              className="absolute z-20 transition-[right,bottom] duration-300"
              style={
                walkthroughStep === 0
                  ? { right: threadOpen ? CHAT_WIDTH + 16 : 16, bottom: 16 }
                  : walkthroughStep === 1
                    ? { right: 20, bottom: 80 }
                    : { right: 20, top: 15 }
              }
            >
              <CommentWalkthrough onDone={dismissWalkthrough} onStepChange={setWalkthroughStep} />
            </div>
          ) : null}
          {/* ScriptResultTabs renders unconditionally — the Script tab (markdown body)
              doesn't depend on AI status, so gating it on aiAnalysis === 'complete'
              left brand staring at "AI Script Review running…" with no way to read the
              script itself. AI-driven tabs gate internally. */}
          {/* x-scroll (for the 870px floor) lives here; y-scroll now lives
              INSIDE ScriptResultTabs, below its own tab bar — so the
              vertical scrollbar only ever runs alongside the script content,
              not the whole height including the tab bar above it. */}
          <div className="flex flex-1 min-w-0 min-h-0 flex-col overflow-x-auto overflow-y-hidden">
            <div className="flex h-full min-w-[870px] flex-col">
              <ScriptResultTabs
                data={stableAnalysis?.data}
                aiStatus={stableAnalysis?.status}
                aiError={stableAnalysis?.error}
                scriptText={scriptText}
                doc={doc}
                scriptTextLoading={scriptTextLoading}
                onAddComment={handleAddToChat}
                onSelectComment={handleSelectToComment}
                highlightAnchors={openAnchors}
                onViewComment={handleViewComment}
                versions={scriptVersions}
                onFetchVersionText={onFetchVersionText}
                canAddLineComment={canAddLineComment}
                jumpToQuote={jumpTarget}
                onJumpedToQuote={handleJumpedToQuote}
                geoEnvelope={geoAnalysis}
                showGeo={showGeo}
                onGenerateGeo={onGenerateGeo}
                geoGenerating={geoGenerating}
                onRegenerateGeo={onRegenerateGeo}
                onSaveGeoSelection={onSaveGeoSelection}
                geoReadOnly={geoReadOnly}
                assignedProducts={assignedProducts}
                campaignProductCount={campaignProductCount}
              />
            </div>
          </div>
          {/* Chat column — always mounted so closing animates out smoothly.
              overflow-hidden + width transition slides it in/out from the right. */}
          <div
            className={cn(
              'relative shrink-0 h-full overflow-hidden ease-in-out transition-[width] duration-300',
              threadOpen ? 'border-l border-border' : 'w-0',
            )}
            style={threadOpen ? { width: CHAT_WIDTH } : undefined}
          >
            <div className="flex h-full min-h-0 flex-col" style={{ width: CHAT_WIDTH }}>
              <ChatThread
                entries={thread}
                enableReplies
                onJumpToAnchor={handleJumpToAnchor}
                scrollToMessageId={scrollToMessage}
                canAddComment={canAddComment}
                locked={locked}
                addCommentBusy={addCommentBusy}
                onAddComment={onAddComment}
                onEditMessage={onEditMessage}
                onDeleteMessage={onDeleteMessage}
                viewerIsSuperAdmin={viewerIsSuperAdmin}
                stagedAnchor={stagedAnchor}
                onDismissAnchor={(sent) =>
                  setStagedAnchor((prev) => (sent === undefined || prev === sent ? undefined : prev))
                }
                outgoingActor={viewerActor}
                draft={draft}
                setDraft={setDraft}
                textareaRef={draftTextareaRef}
              />
            </div>
          </div>
        </div>

        {/* footer review actions removed — Approve is in the
            header (top-right). "Request changes" is now a chat-thread
            comment, posted via the Thread side-panel; no formal status
            change. */}
      </div>
    </>,
    document.body,
  );
}

export default ScriptReviewPanel;
